import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCommunicationSummary } from "../../../../apis/apiEQL";
import { CHART_BLUE, CHART_ORANGE, CHART_SLATE } from "../../chartPalette";

// Los tres estados de la conversación. Son excluyentes y exhaustivos: suman el
// total del período, que es justo lo que un treemap necesita para no mentir.
//
// Colores sólidos de la paleta, sin aclarar: las dos hojas de un estado comparten
// su color y se separan por el gap, que es como se leen los treemaps. El gris
// pizarra del último es deliberado —"sin mensajes" no es una alerta, es la
// ausencia de conversación— y se prefiere sobre el gris claro porque el rótulo
// blanco sobre #94a3b8 no alcanza contraste.
const ESTADOS = [
  { key: "last_message_from_reporter", labelKey: "comm_from_reporter", color: CHART_BLUE },
  { key: "awaiting_reporter_reply", labelKey: "comm_awaiting_reporter", color: CHART_ORANGE },
  { key: "without_messages", labelKey: "comm_no_messages", color: CHART_SLATE },
];

/**
 * useCommunication — estado de la conversación de las denuncias del año, como
 * treemap de dos niveles.
 *
 * Las notas internas cruzan los tres estados en vez de ser un cuarto grupo, así
 * que van como segundo nivel: cada estado se parte en "con notas internas" y
 * "sin notas internas". De esa forma las áreas sí suman lo que dicen sumar, y
 * la gráfica sigue respondiendo las tres preguntas originales.
 *
 * @param {number} year
 * @returns {{ chart: Object|null, loading: boolean }}
 */
export const useCommunication = (year) => {
  const [t] = useTranslation("dashboard");
  const tRef = useRef(t);
  tRef.current = t;

  const build = (resp, tr) => {
    const total = resp.total ?? 0;

    const tree = ESTADOS.map((e) => {
      const count = resp[e.key] ?? 0;
      const conNotas = resp[`${e.key}_with_notes`] ?? 0;
      return {
        label: tr(`static_dashboard.${e.labelKey}`),
        count,
        color: e.color,
        // El desglose por notas internas no da un segundo nivel visual —serían
        // mosaicos demasiado chicos y sin rótulo—, así que vive en el tooltip.
        detail: tr("static_dashboard.comm_notes_detail", { count: conNotas }),
      };
    }).filter((n) => n.count > 0);

    return {
      id: "communication",
      title: tr("static_dashboard.communication_title"),
      type: "treemap",
      chartData: { title: tr("static_dashboard.communication_title"), tree },
      footer: tr("static_dashboard.communication_notes_total", {
        notes: (resp.with_internal_notes ?? 0).toLocaleString(),
      }),
      footerNote: tr("static_dashboard.communication_basis", {
        total: total.toLocaleString(),
      }),
    };
  };

  const cacheRef = useRef(new Map()); // year -> chart
  const [chart, setChart] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    if (cacheRef.current.has(year)) {
      setChart(cacheRef.current.get(year));
      setLoading(false);
      return;
    }

    (async () => {
      setLoading(true);
      try {
        const resp = await getCommunicationSummary(year);
        if (!active) return;
        if (!resp) {
          setChart(null);
          return;
        }

        const built = build(resp, tRef.current);
        cacheRef.current.set(year, built);
        setChart(built);
      } catch (e) {
        // La tarjeta se dibuja igual, pero diciendo que no hubo datos. Inventar
        // números aquí sería peor que no mostrar nada: un 403 del supervisor o
        // un backend sin desplegar se leería como una medición real. No se
        // cachea, así el siguiente intento vuelve a pedir.
        console.error("useCommunication: communication summary failed", e);
        if (active) {
          setChart({
            id: "communication",
            title: tRef.current("static_dashboard.communication_title"),
            emptyMessage: tRef.current("static_dashboard.communication_unavailable"),
          });
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [year]);

  return { chart, loading };
};

export default useCommunication;
